import { useEffect, useState, useMemo } from "react";
import { ListChecks, GanttChart, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

const PRIO_COLORS = { P0: "#E24B4A", P1: "#EF9F27", P2: "#A1A1AA" };
const STATUT_COLORS = { planifie: "#A1A1AA", en_cours: "#378ADD", retard: "#E24B4A", clos: "#1D9E75" };

/**
 * PlansActionGantt — V23.15.44
 *
 * Vue Gantt SVG légère par plan d'action.
 * Ligne horizontale = plan · barre = période de début→échéance colorée selon statut.
 * Barre de progression interne + label priorité + owner.
 */
export default function PlansActionGantt() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/workspace/plans");
        setPlans(r.data?.items || []);
      } catch {
        setPlans([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chart = useMemo(() => {
    if (!plans.length) return null;
    // Parse dates
    const items = plans
      .map((p) => {
        const start = p.date_debut ? new Date(p.date_debut) : (p.created_at ? new Date(p.created_at) : new Date());
        const end = p.echeance ? new Date(p.echeance) : new Date(start.getTime() + 30 * 24 * 3600 * 1000);
        return { ...p, _start: start, _end: end };
      })
      .filter((p) => !isNaN(p._start.getTime()) && !isNaN(p._end.getTime()) && p._end > p._start);

    if (!items.length) return null;

    const minDate = new Date(Math.min(...items.map((i) => i._start.getTime())));
    const maxDate = new Date(Math.max(...items.map((i) => i._end.getTime())));
    const totalMs = Math.max(maxDate - minDate, 1);
    const today = new Date();
    const todayPct = ((today - minDate) / totalMs) * 100;

    // Month ticks
    const ticks = [];
    const cur = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    while (cur <= maxDate) {
      const pct = ((cur - minDate) / totalMs) * 100;
      ticks.push({ pct, label: cur.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }) });
      cur.setMonth(cur.getMonth() + 1);
    }

    return { items, minDate, maxDate, todayPct, ticks };
  }, [plans]);

  if (loading) {
    return (
      <div className="noam-card p-10 text-center text-zinc-400 text-sm" data-testid="plans-gantt-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement du Gantt…
      </div>
    );
  }
  if (!chart) {
    return (
      <div className="noam-card p-10 text-center" data-testid="plans-gantt-empty">
        <GanttChart className="w-10 h-10 text-zinc-600 mx-auto opacity-60" />
        <div className="text-sm text-zinc-300 mt-3">Aucun plan avec dates exploitables</div>
        <div className="text-xs text-zinc-500 mt-1">
          Ajoute une <strong>échéance</strong> à tes plans pour qu&apos;ils apparaissent ici.
        </div>
      </div>
    );
  }

  const { items, todayPct, ticks } = chart;
  const ROW_HEIGHT = 44;

  return (
    <div className="noam-card overflow-hidden" data-testid="plans-gantt">
      <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
        <div className="text-xs text-zinc-400">
          <strong className="text-white">{items.length}</strong> plan{items.length > 1 ? "s" : ""} · vue Gantt
        </div>
        <div className="flex items-center gap-3 text-[10px] text-zinc-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded" style={{ background: STATUT_COLORS.planifie }} /> Planifié</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded" style={{ background: STATUT_COLORS.en_cours }} /> En cours</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded" style={{ background: STATUT_COLORS.retard }} /> Retard</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded" style={{ background: STATUT_COLORS.clos }} /> Clos</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[900px] p-4">
          {/* Timeline header */}
          <div className="relative h-6 mb-2 border-b border-white/10">
            {ticks.map((tk, i) => (
              <div
                key={i}
                className="absolute top-0 h-full flex flex-col items-start"
                style={{ left: `${tk.pct}%` }}
              >
                <div className="w-px h-full bg-white/10" />
                <div className="text-[9px] text-zinc-500 mt-0.5 -translate-x-1 whitespace-nowrap">{tk.label}</div>
              </div>
            ))}
            {/* Today line */}
            {todayPct >= 0 && todayPct <= 100 && (
              <div
                className="absolute top-0 h-full w-px z-20"
                style={{ left: `${todayPct}%`, background: "#EF9F27" }}
                title="Aujourd'hui"
              >
                <div className="text-[9px] font-semibold text-[#EF9F27] absolute top-0 left-1 whitespace-nowrap">
                  Aujourd&apos;hui
                </div>
              </div>
            )}
          </div>

          {/* Rows */}
          <div className="space-y-1 relative">
            {items.map((p, i) => {
              const totalMs = chart.maxDate - chart.minDate;
              const leftPct = ((p._start - chart.minDate) / totalMs) * 100;
              const widthPct = ((p._end - p._start) / totalMs) * 100;
              const color = STATUT_COLORS[p.statut] || "#A1A1AA";
              const progress = p.progression || 0;
              return (
                <div
                  key={p.id || i}
                  className="relative flex items-center h-[44px] rounded hover:bg-white/[0.02] group"
                  data-testid={`plans-gantt-row-${p.id || i}`}
                >
                  {/* Label */}
                  <div className="w-56 shrink-0 pr-3 text-xs">
                    <div className="text-zinc-100 truncate font-medium" title={p.titre}>{p.titre}</div>
                    <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                      {p.priorite && (
                        <span
                          className="px-1 rounded"
                          style={{ background: `${PRIO_COLORS[p.priorite]}22`, color: PRIO_COLORS[p.priorite] }}
                        >
                          {p.priorite}
                        </span>
                      )}
                      {p.owner && <span className="truncate">{p.owner}</span>}
                    </div>
                  </div>
                  {/* Timeline bar area */}
                  <div className="flex-1 relative h-full">
                    <div className="absolute inset-y-0 left-0 right-0 top-1/2 h-px bg-white/[0.04] -translate-y-1/2" />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 rounded-md h-6 group-hover:h-7 transition-all shadow-sm overflow-hidden"
                      style={{
                        left: `${leftPct}%`,
                        width: `${Math.max(widthPct, 2)}%`,
                        background: `${color}30`,
                        border: `1px solid ${color}80`,
                      }}
                      title={`${p.titre} · ${p._start.toLocaleDateString("fr-FR")} → ${p._end.toLocaleDateString("fr-FR")}`}
                    >
                      {/* Progress fill */}
                      <div
                        className="h-full"
                        style={{ width: `${progress}%`, background: color }}
                      />
                      {/* Label inline */}
                      <div className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold text-white/90 whitespace-nowrap">
                        {progress > 0 && `${progress}%`}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
